'use client';

import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Input, Label } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreatePartner } from '@/hooks/usePartners';
import type { Partner } from '@/lib/types';

interface CreatePartnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partners: Partner[];
  /** Null creates an integrator; an id creates a sub-dealer under it. */
  parentId: string | null;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 100);
}

export function CreatePartnerDialog({
  open,
  onOpenChange,
  partners,
  parentId,
}: CreatePartnerDialogProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [contactEmail, setContactEmail] = useState('');

  const createPartner = useCreatePartner();
  const parent = partners.find((candidate) => candidate.id === parentId);

  const reset = () => {
    setName('');
    setSlug('');
    setSlugTouched(false);
    setContactEmail('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !slug.trim()) {
      toast.error('A name and a slug are required');
      return;
    }

    try {
      await createPartner.mutateAsync({
        name: name.trim(),
        slug: slug.trim(),
        parentId,
        contactEmail: contactEmail.trim() || null,
      });
      toast.success(`${name} created`, {
        description: parent
          ? `Sells under ${parent.name} and inherits its Flespi token.`
          : 'Store its Flespi token next, then sync the pool.',
      });
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error('Could not create the partner', {
        description: error instanceof Error ? error.message : 'Something went wrong',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {parent ? `New sub-dealer under ${parent.name}` : 'New integrator'}
            </DialogTitle>
            <DialogDescription>
              {parent
                ? 'A sub-dealer sells on its integrator’s Flespi account, so it needs no token of its own.'
                : 'An integrator holds the Flespi token and the device pool everything else draws from.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="partner-name">Name</Label>
              <Input
                id="partner-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  if (!slugTouched) setSlug(toSlug(event.target.value));
                }}
                placeholder="Nordic Telematics AB"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="partner-slug">Slug</Label>
              <Input
                id="partner-slug"
                value={slug}
                onChange={(event) => {
                  setSlugTouched(true);
                  setSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                }}
                placeholder="nordic-telematics"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="partner-email">Contact email</Label>
              <Input
                id="partner-email"
                type="email"
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
                placeholder="ops@nordic-telematics.se"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createPartner.isPending}>
              {createPartner.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
